// ============================================================================
// Utility: zonedTimeToUtc
//
// Converts a wall-clock date + time-of-day in a given IANA timezone (e.g.
// teams.standup_config.timezone, "America/New_York") into the UTC instant
// it represents. Needed to compare a team's `digest_time` (a local
// "HH:mm" string) against `standup_responses.created_at` /
// `agent_checkin.created_at` (timestamptz, i.e. real UTC instants) for the
// Check-ins page's on-time/missed status — see TeamCheckins.tsx.
//
// No timezone library in this project (checked package.json: date-fns is
// present but not date-fns-tz/luxon), and the one existing server-side
// equivalent (the digest-scheduler Postgres function in
// 20260713120000_add_standup_digest_config.sql) uses Postgres's `AT TIME
// ZONE`, which isn't reachable from the frontend. This reimplements the same
// idea in JS using only Intl.DateTimeFormat, which is a standard technique
// for zone conversion in the browser (no formal timezone database bundled
// with the app).
//
// Accurate for this feature's purposes (a once-daily cutoff comparison);
// like most Intl-based approaches it can be off by up to a minute right at
// a DST transition instant, which doesn't matter for a check-in deadline.
// ============================================================================

/**
 * Returns how many minutes `timeZone` is ahead of UTC at the instant
 * `date` represents (e.g. "America/New_York" in January -> -300).
 */
function getTimezoneOffsetMinutes(date: Date, timeZone: string): number {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })

  const parts = dtf.formatToParts(date).reduce<Record<string, string>>((acc, part) => {
    acc[part.type] = part.value
    return acc
  }, {})

  // Some locales/zones report hour "24" for midnight instead of "00".
  const hour = parts.hour === '24' ? 0 : Number(parts.hour)

  const asUtc = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    hour,
    Number(parts.minute),
    Number(parts.second)
  )

  return (asUtc - date.getTime()) / 60000
}

/**
 * @param dateStr "YYYY-MM-DD"
 * @param timeStr "HH:mm" (24-hour, matches how standup_config stores dm_time/digest_time)
 * @param timeZone IANA zone name, e.g. "America/New_York". Falls back to UTC
 *   for an empty/invalid value the same way the digest-scheduler SQL does
 *   (`COALESCE(t.standup_config->>'timezone', 'UTC')`).
 */
export function zonedTimeToUtc(dateStr: string, timeStr: string, timeZone: string): Date {
  const [year, month, day] = dateStr.split('-').map(Number)
  const [hour, minute] = timeStr.split(':').map(Number)
  const zone = timeZone || 'UTC'

  // First guess: treat the wall-clock time as if it were already UTC, then
  // measure that zone's offset at (approximately) this instant and correct
  // for it. One pass is sufficient outside the rare DST-transition minute.
  const guess = new Date(Date.UTC(year, month - 1, day, hour, minute))
  const offsetMinutes = getTimezoneOffsetMinutes(guess, zone)
  return new Date(guess.getTime() - offsetMinutes * 60000)
}

/** "YYYY-MM-DD" for a given Date, in local (browser) time. */
export function toDateString(date: Date): string {
  return (
    date.getFullYear() +
    '-' +
    String(date.getMonth() + 1).padStart(2, '0') +
    '-' +
    String(date.getDate()).padStart(2, '0')
  )
}

/** "YYYY-MM-DD" for the day before the given "YYYY-MM-DD" string. */
export function previousDateString(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00')
  d.setDate(d.getDate() - 1)
  return toDateString(d)
}

/** Formats a Date as a short local time-of-day, e.g. "9:14 AM", in `timeZone`. */
export function formatTimeInZone(date: Date, timeZone: string): string {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: timeZone || 'UTC',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date)
}

/** Minutes since local midnight for `date`, as seen in `timeZone`. Used to
 *  average submission times without round-tripping through UTC conversion. */
export function getMinutesOfDayInZone(date: Date, timeZone: string): number {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: timeZone || 'UTC',
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
  })
  const parts = dtf.formatToParts(date).reduce<Record<string, string>>((acc, part) => {
    acc[part.type] = part.value
    return acc
  }, {})
  const hour = parts.hour === '24' ? 0 : Number(parts.hour)
  return hour * 60 + Number(parts.minute)
}

/** Formats a minutes-since-midnight value (e.g. from getMinutesOfDayInZone
 *  or an average of several) as "9:14 AM". */
export function formatMinutesOfDay(minutes: number): string {
  const rounded = Math.round(((minutes % 1440) + 1440) % 1440)
  const hour24 = Math.floor(rounded / 60)
  const minute = rounded % 60
  const period = hour24 < 12 ? 'AM' : 'PM'
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12
  return `${hour12}:${String(minute).padStart(2, '0')} ${period}`
}
